<template>
  <div class="mb-3">
    <v-autocomplete
      :value="value"
      :items="listOptions"
      :label="label"
      :disabled="disabled"
      :required="required"
      :error-messages="errorMessages"
      :clearable="clearable"
      :loading="isLoading"
      :hide-no-data="isLoading"
      :no-data-text="$t('components.select.noListText')"
      dense
      hide-details="auto"
      outlined
      @change="onChange"
    >
      <template v-slot:prepend-item v-if="isAdmin">
        <v-list-item ripple @click.stop="onCreateClick">
          <v-list-item-action>
            <v-icon color="primary">mdi-plus</v-icon>
          </v-list-item-action>
          <v-list-item-title>{{ $t('list.createList') }}</v-list-item-title>
        </v-list-item>
        <v-divider class="mt-2" />
      </template>
    </v-autocomplete>

    <selection-dialog
      v-if="showModeDialog"
      v-model="showModeDialog"
      :title="$t('list.selectModeTitle')"
      :confirm-disabled="!selectedMode"
      @close="closeModeDialog"
      @confirm="confirmMode"
    >
      <v-select
        v-model="selectedMode"
        :items="listModeChoices"
        item-text="text"
        item-value="value"
        :label="$t('list.listType')"
        outlined
        dense
      />
    </selection-dialog>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import { ListService } from '@/services/listService'
  import selectionDialog from '@/components/dialogs/SelectionDialog.vue'

  export default {
    name: 'OListSelect',
    components: {
      selectionDialog,
    },
    props: {
      value: {
        type: String,
        default: null,
      },
      label: {
        type: String,
        default: '',
      },
      type: {
        type: String,
        default: 'locations',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      required: {
        type: Boolean,
        default: false,
      },
      errorMessages: {
        type: [String, Array],
        default: () => [],
        validator: (v) => typeof v === 'string' || (Array.isArray(v) && v.every((e) => typeof e === 'string')),
      },
      clearable: {
        type: Boolean,
        default: true,
      },
      /**
       * Pre-fetched lists supplied by the parent so several selectors can share
       * a single API call. When null, the component fetches its own lists on
       * mount (standalone usage).
       */
      items: {
        type: Array,
        default: null,
      },
      /**
       * Loading state of parent-supplied lists. Ignored in standalone mode,
       * where the component tracks its own fetch instead.
       */
      loading: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        lists: [],
        fetching: false,
        showModeDialog: false,
        selectedMode: null,
      }
    },
    async mounted() {
      if (this.items === null) {
        await this.fetchLists()
      }
    },
    computed: {
      ...mapState(['isAdmin']),
      isLoading() {
        return this.items === null ? this.fetching : this.loading
      },
      listOptions() {
        const source = this.items === null ? this.lists : this.items
        return source.map((list) => ({ text: list.name, value: list.list_id }))
      },
      listModeChoices() {
        return [
          { value: 'static', text: this.$t('list.modeStatic') },
          { value: 'dynamic', text: this.$t('list.modeDynamic') },
        ]
      },
    },
    methods: {
      async fetchLists() {
        this.fetching = true
        try {
          const listService = new ListService(this.$auth.accessToken)
          const res = await listService.list({ type: this.type }, false)
          this.lists = res?.data || []
        } finally {
          this.fetching = false
        }
      },
      onChange(value) {
        this.$emit('input', value ?? null)
      },
      onCreateClick() {
        this.selectedMode = null
        this.showModeDialog = true
      },
      closeModeDialog() {
        this.showModeDialog = false
        this.selectedMode = null
      },
      confirmMode() {
        const mode = this.selectedMode
        this.showModeDialog = false
        this.selectedMode = null
        this.$emit('create-list', { mode })
      },
    },
  }
</script>
